import React, { useState, useEffect } from 'react';
import { clientsApi, statementApi, paymentsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import ClientSearch from '../components/ClientSearch';
import { Receipt, Plus, Download, Trash2, Printer } from 'lucide-react';
import { toast } from 'sonner';

const StatementPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'Contanti', payment_date: new Date().toISOString().slice(0, 10), ddt_id: '', note: '' });

  useEffect(() => {
    clientsApi.getAll().then(r => setClients(r.data)).catch(() => toast.error('Errore caricamento clienti'));
  }, []);

  // Auto-load: client selected → per-client; or date-range without client → global
  useEffect(() => {
    if (selectedClientId) {
      loadStatement();
    } else if (dateFrom || dateTo) {
      loadGlobalStatement();
    } else {
      setStatement(null);
    }
  }, [selectedClientId, dateFrom, dateTo]);

  const loadStatement = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await statementApi.get(selectedClientId, params);
      setStatement({ ...r.data, mode: 'client' });
    } catch (err) {
      toast.error('Errore caricamento estratto conto');
    } finally { setLoading(false); }
  };

  const loadGlobalStatement = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await statementApi.getGlobal(params);
      setStatement({ ...r.data, client: { name: 'TUTTI I CLIENTI' }, mode: 'global' });
    } catch (err) {
      toast.error('Errore caricamento estratto conto globale');
    } finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const formatDate = (s) => s ? new Date(s).toLocaleDateString('it-IT') : '-';

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error('Importo non valido'); return; }
    try {
      await paymentsApi.create({
        ddt_id: paymentForm.ddt_id || null,
        client_id: selectedClientId,
        client_name: statement?.client?.name || '',
        amount: parseFloat(paymentForm.amount),
        method: paymentForm.method,
        payment_date: paymentForm.payment_date,
        note: paymentForm.note,
      });
      toast.success('Pagamento registrato');
      setPaymentDialog(false);
      setPaymentForm({ amount: '', method: 'Contanti', payment_date: new Date().toISOString().slice(0, 10), ddt_id: '', note: '' });
      loadStatement();
    } catch { toast.error('Errore'); }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Eliminare questo pagamento?')) return;
    try {
      await paymentsApi.delete(id);
      toast.success('Pagamento eliminato');
      loadStatement();
    } catch { toast.error('Errore'); }
  };

  const handlePrint = () => window.print();

  const handleDownloadCsv = () => {
    if (!statement) return;
    const rows = [['Data', 'Tipo', 'Doc/Rif', 'Descrizione', 'Importo', 'Pagato', 'Resto']];
    statement.ddt_list.forEach(d => {
      const p = d.paid_amount || 0;
      rows.push([formatDate(d.date), 'DDT', `${d.number}-${d.year}`, d.internal_ref || '', d.total_amount, p, (d.total_amount - p)]);
    });
    statement.payments.filter(p => !p.ddt_id).forEach(p => {
      rows.push([formatDate(p.payment_date), 'Pagamento', '', `${p.method} ${p.note || ''}`, '', p.amount, '']);
    });
    rows.push(['', '', '', 'TOTALE', statement.total_invoiced, statement.total_paid, statement.balance]);
    const csv = rows.map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `estratto_conto_${statement.client.name}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="statement-page" className="space-y-6">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Estratto Conto</h1>
          <p className="text-muted-foreground text-sm">Saldo, DDT e pagamenti per cliente</p>
        </div>
        {statement && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="w-4 h-4 mr-2" />Stampa</Button>
            <Button variant="outline" size="sm" onClick={handleDownloadCsv}><Download className="w-4 h-4 mr-2" />Scarica CSV</Button>
            <Button size="sm" onClick={() => setPaymentDialog(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Aggiungi Pagamento</Button>
          </div>
        )}
      </div>

      <Card className="card-swiss no-print">
        <CardContent className="pt-6 space-y-3">
          <Label className="label-uppercase">Cliente</Label>
          <ClientSearch clients={clients} value={selectedClientId} onChange={setSelectedClientId} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Dal</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Al</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center text-muted-foreground py-12">Caricamento...</div>}

      {statement && (
        <div className="print-area space-y-6">
          {/* Print-only header */}
          <div className="print-only mb-3">
            <h1 style={{ fontSize: '14pt', fontWeight: 'bold', margin: 0 }}>ESTRATTO CONTO</h1>
            <p style={{ fontSize: '10pt', margin: '2px 0' }}>
              Cliente: <b>{statement.client?.name || 'TUTTI'}</b>
              {(dateFrom || dateTo) && ` · Periodo: ${formatDate(dateFrom) || '...'} - ${formatDate(dateTo) || '...'}`}
              {' · Stampato il: '}{new Date().toLocaleDateString('it-IT')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 no-print">
            <Card className="card-swiss">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Totale Fatturato</p>
                <p className="text-2xl font-bold font-['JetBrains_Mono']">{formatCurrency(statement.total_invoiced)}</p>
              </CardContent>
            </Card>
            <Card className="card-swiss bg-green-50/50 border-green-200/50">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-green-700">Totale Pagato</p>
                <p className="text-2xl font-bold font-['JetBrains_Mono'] text-green-700">{formatCurrency(statement.total_paid)}</p>
              </CardContent>
            </Card>
            <Card className={`card-swiss ${statement.balance > 0 ? 'bg-orange-50/50 border-orange-200/50' : 'bg-blue-50/50 border-blue-200/50'}`}>
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-orange-700">Saldo Aperto</p>
                <p className={`text-2xl font-bold font-['JetBrains_Mono'] ${statement.balance > 0 ? 'text-orange-700' : 'text-blue-700'}`}>{formatCurrency(statement.balance)}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="card-swiss">
            <CardHeader className="no-print"><CardTitle className="text-lg font-semibold">DDT — {statement.client?.name || 'TUTTI I CLIENTI'}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header-swiss">Data</TableHead>
                    <TableHead className="table-header-swiss">N° DDT</TableHead>
                    {statement.mode === 'global' && <TableHead className="table-header-swiss">Cliente</TableHead>}
                    <TableHead className="table-header-swiss">Rif. Interno</TableHead>
                    <TableHead className="table-header-swiss text-right">Totale</TableHead>
                    <TableHead className="table-header-swiss text-right">Pagato</TableHead>
                    <TableHead className="table-header-swiss text-right">Resto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.ddt_list.length === 0 && (
                    <TableRow><TableCell colSpan={statement.mode === 'global' ? 7 : 6} className="text-center py-8 text-muted-foreground">Nessun DDT nel periodo</TableCell></TableRow>
                  )}
                  {statement.ddt_list.map((d) => {
                    const p = d.paid_amount || 0;
                    const rawR = (d.total_amount || 0) - p;
                    const r = Math.abs(rawR) < 0.01 ? 0 : rawR;
                    return (
                      <TableRow key={d.id}>
                        <TableCell className="table-cell-swiss">{formatDate(d.date)}</TableCell>
                        <TableCell className="table-cell-swiss font-['JetBrains_Mono']">{d.number}-{d.year}</TableCell>
                        {statement.mode === 'global' && <TableCell className="table-cell-swiss">{d.client_name}</TableCell>}
                        <TableCell className="table-cell-swiss">{d.internal_ref || '-'}</TableCell>
                        <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">{formatCurrency(d.total_amount)}</TableCell>
                        <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono'] text-green-700">{formatCurrency(p)}</TableCell>
                        <TableCell className={`table-cell-swiss text-right font-['JetBrains_Mono'] font-semibold ${r > 0 ? 'text-orange-600' : 'text-green-600'}`}>{r > 0 ? formatCurrency(r) : '✓'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {statement.ddt_list.length > 0 && (
                  <tfoot className="print-totals">
                    <TableRow>
                      <TableCell colSpan={statement.mode === 'global' ? 4 : 3} className="text-right font-bold">TOTALE</TableCell>
                      <TableCell className="text-right font-['JetBrains_Mono'] font-bold">{formatCurrency(statement.total_invoiced)}</TableCell>
                      <TableCell className="text-right font-['JetBrains_Mono'] font-bold text-green-700">{formatCurrency(statement.ddt_list.reduce((s, d) => s + (d.paid_amount || 0), 0))}</TableCell>
                      <TableCell className="text-right font-['JetBrains_Mono'] font-bold">{formatCurrency(statement.total_invoiced - statement.ddt_list.reduce((s, d) => s + (d.paid_amount || 0), 0))}</TableCell>
                    </TableRow>
                  </tfoot>
                )}
              </Table>
            </CardContent>
          </Card>

          <div className="text-xs text-muted-foreground no-print">
            💡 Per vedere il <b>dettaglio degli acconti</b> (data, modalità, note) vai alla pagina <a href="/movimenti" className="underline font-medium">Movimenti</a>.
          </div>
        </div>
      )}

      {!statement && !loading && (
        <div className="text-center py-16 text-muted-foreground">
          <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Seleziona un cliente <b>oppure</b> imposta una fascia data per vedere tutti i DDT del periodo</p>
        </div>
      )}

      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo Pagamento</DialogTitle></DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Importo (€)</Label>
                <Input data-testid="payment-amount-input" type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Modalità</Label>
                <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Contanti">Contanti</SelectItem>
                    <SelectItem value="Bonifico">Bonifico</SelectItem>
                    <SelectItem value="Assegno">Assegno</SelectItem>
                    <SelectItem value="Carta">Carta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Data Pagamento</Label>
              <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Associa a DDT (opzionale)</Label>
              <Select value={paymentForm.ddt_id || 'none'} onValueChange={(v) => setPaymentForm({ ...paymentForm, ddt_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Pagamento generico" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Pagamento generico</SelectItem>
                  {(statement?.ddt_list || []).map(d => (
                    <SelectItem key={d.id} value={d.id}>DDT {d.number}-{d.year} ({formatCurrency(d.total_amount)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
              <Input value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} placeholder="Opzionale" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialog(false)}>Annulla</Button>
              <Button type="submit" data-testid="payment-save-btn" className="btn-primary">Registra</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatementPage;
